import RedCircleWithWhiteCrossIllustration from 'assets/svg/circle/red-circle-with-white-cross-illustration.svg'
import ErrorIllustration from 'assets/svg/error/error-illustration-big.svg'
import Flex from 'common/components/Flex'
import Header from 'common/components/Header'
import Layout from 'common/components/Layout'
import MainFooter from 'common/components/MainFooter/RevampFooter'
import Pagination from 'common/components/Pagination'
import Skeleton from 'common/components/Skeleton'
import Typography from 'common/components/Typography'
import { WEB_DESKTOP_RALALI_URL } from 'common/constants'
import theme from 'common/theme'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { useStoreActions, useStoreState } from 'stores/hooks'
import styled from 'styled-components'

const Container = styled.section`
  width: ${theme.layout.containerWidth};
  margin: 32px auto 80px;
`

const PopularSearchListSkeleton = styled.div`
  margin: 40px 0 64px;
  display: grid;
  grid-template-columns: 50% 50%;
  gap: 16px;
  > * {
    width: 240px;

    &:nth-of-type(3n - 1) {
      width: 280px;
    }
    &:nth-of-type(3n + 0) {
      width: 378px;
    }
  }
`

const PopularSearchList = styled.div`
  margin: 40px 0 64px;
  display: grid;
  grid-template-columns: 50% 50%;
  gap: 16px;
`

const ListItemPopularSearch = styled.a`
  :hover {
    > * {
      color: ${theme.colors.orange};
      text-decoration: underline;
    }
  }
`

const fiftyItemsInArray = Array.from(
  { length: 50 },
  (element, index) => index,
)
const twelveItemsInArray = Array.from(
  { length: 12 },
  (element, index) => index,
)

interface IPopularSearch {
  query: {
    page?: number
  }
}

function PopularSearch({ query }: IPopularSearch): JSX.Element {
  const router = useRouter()
  const popularSearchState = useStoreState(
    (state) => state.popularSearch,
  )
  const popularSearchAction = useStoreActions(
    (actions) => actions.popularSearch,
  )
  const titlePage = `Daftar Pencarian Populer${
    query?.page > 1 ? ` Halaman ${query.page}` : ''
  }`

  const handlePageChange = (page) => {
    router.push({
      pathname: router.pathname,
      query: {
        ...(page > 1 && { page }),
      },
    })
  }

  useEffect(() => {
    if (router.isReady) {
      popularSearchAction.getPopularSearch({
        limit: 50,
        sort_by: 'newest',
        ...router.query,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query])

  return (
    <Layout
      header={{ component: <Header /> }}
      footer={{ component: <MainFooter /> }}
      seo={{
        title: `${titlePage} | Ralali.com`,
        description: '',
        additionalMetaTags: [
          {
            name: 'viewport',
            content: 'width=device-width, initial-scale=0',
          },
        ],
      }}
    >
      <Container>
        <Typography type="bh1" textAlign="center">
          {titlePage}
        </Typography>

        {popularSearchState.isLoading && (
          <>
            <PopularSearchListSkeleton data-testid="popular-search-skeleton-test">
              {fiftyItemsInArray.map((index) => {
                return <Skeleton key={index} height="14px" />
              })}
            </PopularSearchListSkeleton>

            <Flex justifyContent="center">
              {twelveItemsInArray.map((index) => {
                return (
                  <Skeleton
                    key={index}
                    width="26px"
                    height="26px"
                    marginRight="8px"
                  />
                )
              })}
            </Flex>
          </>
        )}

        {!popularSearchState.isLoading && popularSearchState.isError && (
          <Flex
            alignItems="center"
            flexDirection="column"
            margin="64px 0"
            data-testid="popular-search-error-test"
          >
            <ErrorIllustration />
            <Typography
              type="rparagraph"
              fontSize="16px"
              color="grayDark"
              marginTop={24}
            >
              Mohon maaf terjadi kesalahan. Silahkan coba beberapa
              saat lagi.
            </Typography>
          </Flex>
        )}

        {!popularSearchState.isLoading &&
          !popularSearchState.isError &&
          popularSearchState.data?.length === 0 && (
            <Flex
              alignItems="center"
              flexDirection="column"
              margin="64px 0"
              data-testid="popular-search-empty-test"
            >
              <RedCircleWithWhiteCrossIllustration />
              <Typography
                type="rparagraph"
                fontSize="16px"
                color="grayDark"
                marginTop={24}
              >
                Mohon maaf data tidak ditemukan di sistem.
              </Typography>
            </Flex>
          )}

        {!popularSearchState.isLoading &&
        !popularSearchState.isError &&
        popularSearchState.data?.length > 0 ? (
          <>
            <PopularSearchList>
              {popularSearchState.data.map((popularSearch) => {
                return (
                  <ListItemPopularSearch
                    key={popularSearch.keyword}
                    href={`${WEB_DESKTOP_RALALI_URL}/search/${popularSearch.keyword}`}
                  >
                    <Typography type="rparagraph" maxLine={1}>
                      {popularSearch.keyword}
                    </Typography>
                  </ListItemPopularSearch>
                )
              })}
            </PopularSearchList>

            {popularSearchState?.meta?.total_count >
              popularSearchState?.meta?.limit && (
              <Flex justifyContent="center">
                <Pagination
                  totalData={popularSearchState.meta?.total_count}
                  dropdownValues={[popularSearchState.meta?.limit]}
                  onChange={handlePageChange}
                  initialPage={popularSearchState.meta?.current_page}
                  renderHiddenPaginationLink
                  hiddenPaginationLinkPath={router.pathname}
                />
              </Flex>
            )}
          </>
        ) : null}
      </Container>
    </Layout>
  )
}

export default PopularSearch
